import { Grid, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import TabsSection from "./TabSections";
import LeadCard from "./LeadDetailsCard";
import { useParams } from "react-router-dom";
import { callAPI, Method, updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { OverViewDeals } from "./OverViewDeals";
import { OverviewCalls } from "./OverviewCalls";
import { OverViewNotes } from "./OverViewNotes";
import { OverViewEmail } from "./OverViewEmail";
import { OverViewTask } from "./OverViewTask";
import { OverViewMeetings } from "./OverViewMeetings";

export const LeadDetails: React.FC<any> = React.memo((props) => {
  const params = useParams();
  const id = params?.id;

  const [leadData, setLeadData] = useState<any>();
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [showAllSections, setShowAllSections] = useState<boolean>(false);

  useEffect(() => {
    updateMainHeader(id ? "Edit" : 'Add');

    breadCrumbsStore.updateBreadCrumbs([
      { "Leads": "/leads/list" },
      { [id ? "Edit" : 'Add']: '' },

    ]);
  });

  useEffect(() => {
    async function getLeads() {

      const response: any = await callAPI('leads', Method.GET, null, id)
      if (response) {
        setLeadData(response)
      }

    }
    getLeads();
  }, [id])


  const navItems = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'notes', label: 'Notes', icon: '📋' },
    { id: 'emails', label: 'Emails', icon: '✉️' },
    { id: 'calls', label: 'Calls', icon: '📞' },
    { id: 'tasks', label: 'Tasks', icon: '✅' },
    { id: 'meetings', label: 'Meetings', icon: '👥' },
  ];

  const visibleItems = showAllSections
    ? navItems
    : navItems.filter((item, index) => index < 4 || item.id === activeSection);

  const hiddenItemsCount = navItems.length - visibleItems.length;

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'overview':
        return <OverViewDeals />;
      case 'notes':
        return <OverViewNotes />;
      case 'emails':
        return <OverViewEmail />;
      case 'calls':
        return <OverviewCalls />;
      case 'tasks':
        return <OverViewTask />;
      case 'meetings':
        return <OverViewMeetings />;
      default:
        return <OverViewDeals />;
    }
  };


  return (
    <Grid sx={{ backgroundColor: "#f4f4f9", minHeight: "75vh", p: 2 }}>
      {/* <HeaderList title="Leads > Details"  type="card" position="relative"  isDivider isBackButton url="/leads/list" /> */}

      {leadData && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <LeadCard data={leadData} />
        </Grid>
      )}

      <Grid container spacing={2}>
        {/* Sidebar Navigation */}
        <Grid item xs={12} md={3}>
          <Grid sx={{
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 1,
            p: 2,
            height: 'fit-content'
          }}>
            <Typography variant="h6" sx={{ color: "#7D79C2", mb: 1 }}>
              Sections
            </Typography>
            <List>
              {visibleItems.map((item) => (
                <ListItem
                  key={item.id}
                  disablePadding
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    backgroundColor: activeSection === item.id ? '#f0f0ff' : 'transparent',
                    '&:hover': { backgroundColor: '#f8f8ff' }
                  }}
                >
                  <ListItemButton
                    onClick={() => setActiveSection(item.id)}
                    sx={{
                      py: 1,
                      borderRadius: 1,
                      color: activeSection === item.id ? '#7D79C2' : 'inherit'
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontWeight: activeSection === item.id ? 600 : 400,
                        fontSize: '0.9rem'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}

              {/* Show More / Show Less Button */}
              {!showAllSections && hiddenItemsCount > 0 && (
                <ListItem disablePadding sx={{ mt: 1 }}>
                  <ListItemButton
                    onClick={() => setShowAllSections(true)}
                    sx={{
                      py: 1,
                      borderRadius: 1,
                      backgroundColor: '#f8f8ff',
                      '&:hover': { backgroundColor: '#f0f0ff' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      ⬇️
                    </ListItemIcon>
                    <ListItemText
                      primary={`Show More (${hiddenItemsCount})`}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        color: '#7D79C2',
                        fontSize: '0.9rem'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}

              {showAllSections && (
                <ListItem disablePadding sx={{ mt: 1 }}>
                  <ListItemButton
                    onClick={() => setShowAllSections(false)}
                    sx={{
                      py: 1,
                      borderRadius: 1,
                      backgroundColor: '#f8f8ff',
                      '&:hover': { backgroundColor: '#f0f0ff' }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      ⬆️
                    </ListItemIcon>
                    <ListItemText
                      primary="Show Less"
                      primaryTypographyProps={{
                        fontWeight: 500,
                        color: '#7D79C2',
                        fontSize: '0.9rem'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </List>
          </Grid>
        </Grid>

        {/* Main Content Area */}
        <Grid item xs={12} md={9}>
          <Grid container spacing={2}>
            {/* Main Content Section */}
            <Grid item xs={12} lg={8}>
              {renderActiveSection()}
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} lg={4}>
              {leadData && <Sidebar data={leadData} />}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
});
